import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Package, QrCode, Truck, User, Clock, MapPin, Phone, Camera,
  Check, X, Navigation, Image as ImageIcon, Upload, AlertCircle,
  ChevronDown, ChevronUp, Maximize2, RefreshCw, Loader2
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import QRCode from 'qrcode';
import jsQR from 'jsqr';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderPickupUser = ({ user, token: propToken }) => {
  const token = propToken || localStorage.getItem('auth_token');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [qrCodes, setQrCodes] = useState({});
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanningText, setScanningText] = useState('Point camera at QR code...');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const isDriver = user?.role === 'driver';
  const [cameraReady, setCameraReady] = useState(false);

  // Start camera function - defined before useEffect
  const initializeCamera = useCallback(async () => {
    try {
      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', 
          width: { ideal: 1280 }, 
          height: { ideal: 720 } 
        }
      });
      
      console.log('Camera stream obtained');
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, starting playback');
          videoRef.current.play();
          setCameraReady(true);
          setScanning(true);
          // Start QR scanning loop
          startQRScanning();
        };
      }
      setShowScanner(true);
      return true;
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error('Camera access denied. Please allow camera permissions to scan QR codes.');
      return false;
    }
  }, []);

  // QR Scanning loop
  const startQRScanning = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    scanIntervalRef.current = setInterval(() => {
      if (!videoRef.current || !canvasRef.current || !cameraReady) return;
      
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // Use jsQR to detect QR code
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
        
        if (code) {
          setScanningText('QR Code detected!');
          console.log('QR Code detected:', code.data);
          handleQRCodeDetected(code.data);
        }
      }
    }, 100);
  }, [cameraReady]);

  // Handle detected QR code
  const handleQRCodeDetected = async (qrData) => {
    // Stop scanning while processing
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setScanning(false);
    setScanningText('Verifying...');

    try {
      const response = await fetch(`${API_URL}/api/pickup/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scanned_qr_code: qrData
        })
      });

      const result = await response.json();
      
      if (response.ok && result.matched) {
        setScanResult({ ...result, matched: true });
        toast.success('QR Code Verified! Order confirmed.');
        stopCamera();
        fetchOrders();
      } else {
        toast.error(result.message || 'QR code not recognized');
        setScanningText('Try again...');
        setScanning(true);
        startQRScanning();
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Verification failed. Please try again.');
      setScanningText('Error - Try again...');
      setScanning(true);
      startQRScanning();
    }
  };

  // Auto-start camera when page loads
  useEffect(() => {
    fetchOrders();
    
    // Auto-start camera after component mounts
    const timer = setTimeout(() => {
      initializeCamera();
    }, 300);
    
    return () => {
      clearTimeout(timer);
      stopCamera();
    };
  }, [initializeCamera]);

  useEffect(() => {
    orders.forEach(async (order) => {
      if (!qrCodes[order.id]) {
        try {
          const qrDataUrl = await QRCode.toDataURL(order.qr_code_data || `UPLANDS-PICKUP-${order.id}`, {
            width: 200, margin: 2
          });
          setQrCodes(prev => ({ ...prev, [order.id]: qrDataUrl }));
        } catch (err) {
          console.error('QR generation error:', err);
        }
      }
    });
  }, [orders]);

  const fetchOrders = async () => {
    const endpoint = isDriver ? '/api/pickup/driver/orders' : '/api/pickup/customer/orders';
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  // Use the unified camera initialization
  const startCamera = () => {
    initializeCamera();
  };

  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowScanner(false);
    setScanning(false);
    setCameraReady(false);
    setScanningText('Point camera at QR code...');
  };

  const handleManualScan = async (qrData) => {
    if (!selectedOrder) {
      toast.error('Please select an order first');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/pickup/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: selectedOrder.id,
          scanned_qr_code: qrData
        })
      });

      const result = await response.json();
      setScanResult(result);

      if (result.matched) {
        toast.success('QR Code Matched!');
        stopCamera();
        fetchOrders();
      } else {
        toast.error('QR Code does not match');
      }
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  const handleStartNavigation = async (order) => {
    try {
      const response = await fetch(`${API_URL}/api/pickup/start-navigation?order_id=${order.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Navigation started!');
        
        // Open Google Maps with destination
        const address = encodeURIComponent(
          `${data.destination.address}, ${data.destination.city} ${data.destination.postal_code}`
        );
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
        
        fetchOrders();
      }
    } catch (error) {
      toast.error('Failed to start navigation');
    }
  };

  const handleProofPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setProofImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadDeliveryProof = async () => {
    if (!proofImage || !selectedOrder) return;

    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/pickup/delivery-proof`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: selectedOrder.id,
          image_data: proofImage,
          proof_type: 'delivery',
          notes: 'Delivery completed'
        })
      });

      if (response.ok) {
        toast.success('Delivery proof uploaded! Order marked as delivered.');
        setShowProofUpload(false);
        setProofImage(null);
        setSelectedOrder(null);
        fetchOrders();
      } else {
        toast.error('Failed to upload proof');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'ready_for_pickup': 'bg-green-100 text-green-700',
      'out_for_delivery': 'bg-blue-100 text-blue-700',
      'driver_assigned': 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="order-pickup-user">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-7 h-7 text-emerald-600" />
                ORDER PICKUP
              </h1>
              <p className="text-gray-500 text-sm">
                {isDriver ? 'Orders assigned to you for delivery' : 'Your orders ready for pickup'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Camera Scanner Button */}
        <Card className="p-4 mb-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">Scan QR Code to Verify Pickup</h2>
              <p className="text-emerald-100 text-sm">Point your camera at the admin's QR code</p>
            </div>
            <Button 
              onClick={startCamera}
              className="bg-white text-emerald-600 hover:bg-emerald-50"
              data-testid="scan-qr-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              Open Scanner
            </Button>
          </div>
        </Card>

        {/* Orders List */}
        {orders.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">No Orders for Pickup</h3>
            <p className="text-gray-500 mt-2">
              {isDriver ? 'No orders assigned to you yet' : 'No orders ready for pickup'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card 
                key={order.id}
                className={`p-4 transition-all ${selectedOrder?.id === order.id ? 'ring-2 ring-emerald-500' : ''}`}
                onClick={() => setSelectedOrder(order)}
                data-testid={`pickup-order-${order.id}`}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  {/* QR Code */}
                  <div className="flex-shrink-0 flex justify-center">
                    {qrCodes[order.id] ? (
                      <img src={qrCodes[order.id]} alt="QR" className="w-28 h-28 border rounded-lg" />
                    ) : (
                      <div className="w-28 h-28 bg-gray-100 rounded-lg flex items-center justify-center">
                        <QrCode className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Order Details */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        Order #{order.order_number || order.id.slice(-8).toUpperCase()}
                      </h3>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>

                    {/* Products */}
                    <div className="mb-2">
                      <p className="text-sm text-gray-600">
                        {order.items?.length || 0} items • <strong>${order.total?.toFixed(2)}</strong>
                      </p>
                      <div className="text-xs text-gray-500 mt-1">
                        {(order.items || []).slice(0, 3).map((item, i) => (
                          <span key={i}>{item.name}{i < 2 && order.items.length > 1 ? ', ' : ''}</span>
                        ))}
                        {order.items?.length > 3 && <span> +{order.items.length - 3} more</span>}
                      </div>
                    </div>

                    {/* Customer Info (for drivers) */}
                    {isDriver && order.customer_name && (
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {order.customer_name}
                        </span>
                        {order.customer_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {order.customer_phone}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Delivery Address (for drivers) */}
                    {isDriver && order.delivery_address && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-red-500" />
                        <span>{order.delivery_address.address}, {order.delivery_address.city}</span>
                      </div>
                    )}

                    <p className="text-xs text-gray-400 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatDate(order.created_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 justify-center">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                        startCamera();
                      }}
                    >
                      <Camera className="w-4 h-4 mr-1" />
                      Scan
                    </Button>
                    
                    {isDriver && order.status === 'out_for_delivery' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartNavigation(order);
                          }}
                        >
                          <Navigation className="w-4 h-4 mr-1" />
                          Navigate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                            setShowProofUpload(true);
                          }}
                        >
                          <ImageIcon className="w-4 h-4 mr-1" />
                          Photo Proof
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* QR Scanner Modal - Full Screen on Mobile */}
        {showScanner && (
          <div className="fixed inset-0 bg-black flex flex-col z-50">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Camera className="w-6 h-6" />
                Scan QR Code
              </h2>
              <Button variant="ghost" className="text-white hover:bg-white/20" onClick={stopCamera}>
                <X className="w-6 h-6" />
              </Button>
            </div>

            {/* Camera View - Full Screen */}
            <div className="flex-1 relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Scanning Frame Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="w-64 h-64 md:w-72 md:h-72 border-4 border-emerald-400 rounded-2xl shadow-lg shadow-emerald-500/30" />
                  {/* Corner accents */}
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
                  {/* Scanning line animation */}
                  {scanning && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse" 
                         style={{animation: 'scan 2s ease-in-out infinite'}} />
                  )}
                </div>
              </div>
              
              {/* Scanning Status */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-6 pb-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 bg-emerald-600/90 px-4 py-2 rounded-full">
                    {scanning ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Check className="w-5 h-5 text-white" />
                    )}
                    <p className="text-white font-medium">{scanningText}</p>
                  </div>
                  <p className="text-white/70 text-sm text-center">
                    Point your camera at the QR code on the admin's screen
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="p-4 bg-black/90 space-y-3">
              {/* Selected Order Info */}
              {selectedOrder && (
                <div className="bg-emerald-900/50 border border-emerald-500/50 rounded-lg p-3 text-center">
                  <p className="text-emerald-100 text-sm">
                    Scanning for: <span className="font-bold">Order #{selectedOrder.order_number || selectedOrder.id.slice(-8).toUpperCase()}</span>
                  </p>
                </div>
              )}
              
              {/* Manual Input Option */}
              <details className="group">
                <summary className="text-white/70 text-sm text-center cursor-pointer hover:text-white list-none flex items-center justify-center gap-2">
                  <span>Having trouble?</span>
                  <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-3 bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Enter code manually:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="UPLANDS-PICKUP-..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleManualScan(e.target.value);
                        }
                      }}
                      data-testid="manual-qr-input"
                    />
                    <Button 
                      size="sm"
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        handleManualScan(input.value);
                      }}
                    >
                      Verify
                    </Button>
                  </div>
                </div>
              </details>
            </div>
          </div>
        )}

        {/* Scan Result Modal */}
        {scanResult && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-8 text-center">
              {scanResult.matched ? (
                <>
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-12 h-12 text-green-600" />
                  </div>
                  <h2 className="text-4xl font-bold text-green-600 mb-2">MATCHED</h2>
                  <p className="text-gray-600 mb-4">{scanResult.message}</p>
                  <p className="text-sm text-gray-500">
                    Order #{selectedOrder?.order_number || selectedOrder?.id.slice(-8).toUpperCase()}
                  </p>
                  
                  {isDriver && (
                    <div className="mt-6 space-y-3">
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          handleStartNavigation(selectedOrder);
                          setScanResult(null);
                        }}
                      >
                        <Navigation className="w-5 h-5 mr-2" />
                        Start Navigation to Customer
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-12 h-12 text-red-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-red-600 mb-2">NOT MATCHED</h2>
                  <p className="text-gray-600">{scanResult.message}</p>
                </>
              )}
              
              <Button 
                variant="outline" 
                className="mt-6"
                onClick={() => setScanResult(null)}
              >
                Close
              </Button>
            </Card>
          </div>
        )}

        {/* Delivery Proof Upload Modal */}
        {showProofUpload && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Upload Delivery Proof</h2>
                <Button variant="ghost" onClick={() => { setShowProofUpload(false); setProofImage(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Take a photo of the delivered package as proof of delivery.
                This will be sent to the admin, customer, and saved for your records.
              </p>

              {/* Image Preview or Upload */}
              {proofImage ? (
                <div className="mb-4">
                  <img src={proofImage} alt="Delivery proof" className="w-full rounded-lg" />
                  <Button 
                    variant="outline" 
                    className="mt-2 w-full"
                    onClick={() => setProofImage(null)}
                  >
                    Take Another Photo
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 mb-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Tap to take photo</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleProofPhoto}
                  />
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowProofUpload(false); setProofImage(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={!proofImage || uploading}
                  onClick={uploadDeliveryProof}
                >
                  {uploading ? 'Uploading...' : 'Upload & Complete'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPickupUser;
